import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Power, Loader2, Clapperboard } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const REDIRECT_S = 3;

export const EndPartyButton = ({ token, filmId }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const end = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/watch-party/${token}/end`);
      toast.success("Watch Party ended — you can restart it from the film page before it expires");
      navigate(`/film/${filmId}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't end the party");
      setBusy(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-400/40 text-red-300 hover:bg-red-500/15 text-xs font-semibold transition-colors"
          data-testid="wp-end-party-btn"
        >
          <Power className="w-3.5 h-3.5" /> End Party
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-surface border-white/10" data-testid="wp-end-party-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">End this Watch Party for everyone?</AlertDialogTitle>
          <AlertDialogDescription>
            All viewers will be removed from the room and sent back to the film page. Your remaining time is kept — you can restart the party from the film page before it expires.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="wp-end-party-cancel">Keep watching</AlertDialogCancel>
          <AlertDialogAction onClick={end} disabled={busy} className="bg-red-500 hover:bg-red-600 text-white" data-testid="wp-end-party-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />} End Party
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const WatchPartyEnded = ({ filmId, immediate = false }) => {
  const navigate = useNavigate();
  const [left, setLeft] = useState(immediate ? 0 : REDIRECT_S);
  const dest = filmId ? `/film/${filmId}` : "/";

  useEffect(() => {
    if (left <= 0) { navigate(dest, { replace: true }); return undefined; }
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left, dest, navigate]);

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-6" data-testid="wp-party-ended">
      <div className="max-w-md text-center">
        <Clapperboard className="w-14 h-14 mx-auto text-terracotta mb-4" />
        <h1 className="font-display text-3xl font-black mb-2">The Watch Party has ended</h1>
        <p className="text-foreground/60 text-sm">The host has ended this Watch Party. Thanks for watching together!</p>
        <p className="text-xs text-foreground/45 mt-4" data-testid="wp-ended-countdown">Taking you to the film page in {left}s…</p>
        <button type="button" onClick={() => navigate(dest, { replace: true })} className="mt-3 text-sm text-gold hover:underline" data-testid="wp-ended-go-now">
          Go now
        </button>
      </div>
    </div>
  );
};
